import { useState } from "react";
import { format } from "date-fns";
import { Download, FileText, Loader2, Paperclip } from "lucide-react";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useEpicDependencies,
  useEpicRequestDependencies,
  useDependencyDocument,
} from "@/hooks/queries/use-dependency-documents";
import {
  formatDocumentSize,
  saveDependencyDocument,
  type DependencyDocumentMeta,
} from "@/lib/api/dependency-documents";
import { cn } from "@/lib/utils";

export interface DependencyDocumentsPanelProps {
  parentType: "epic_request" | "epic";
  parentId: string;
  className?: string;
}

/**
 * Read-only panel listing the precursor documents attached to an Epic Request
 * or an Epic.
 *
 * One component serves both detail pages: `parentType` only selects which list
 * hook supplies the rows, and everything below that is shared so the two pages
 * cannot drift. Uploading, deleting, and editing are deliberately absent — the
 * agent flow (`manage_dependencies`) is the only intended producer.
 */
export function DependencyDocumentsPanel({
  parentType,
  parentId,
  className,
}: DependencyDocumentsPanelProps) {
  // Both hooks are called unconditionally to satisfy the rules of hooks; the
  // one for the other parent type is disabled by passing an empty id.
  const epicRequestQuery = useEpicRequestDependencies(
    parentType === "epic_request" ? parentId : ""
  );
  const epicQuery = useEpicDependencies(parentType === "epic" ? parentId : "");
  const query = parentType === "epic_request" ? epicRequestQuery : epicQuery;

  const [previewId, setPreviewId] = useState<string | null>(null);
  // A Set rather than a single id: each row's spinner and disabled state must
  // track its own request. With one shared id, starting a second download while
  // the first was in flight let the first one's `finally` clear the flag, so the
  // second row's spinner vanished and its button re-enabled mid-request.
  const [downloadingIds, setDownloadingIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const documents = query.data ?? [];

  const handleDownload = async (doc: DependencyDocumentMeta) => {
    setDownloadingIds((current) => new Set(current).add(doc.id));
    setDownloadError(null);
    try {
      await saveDependencyDocument(doc.id, doc.fileName);
    } catch {
      setDownloadError(`Could not download ${doc.fileName}.`);
    } finally {
      setDownloadingIds((current) => {
        const next = new Set(current);
        next.delete(doc.id);
        return next;
      });
    }
  };

  // Rendered as a chrome-free section rather than a Card: both host pages
  // already supply their own card container, matching ComplianceDetailSection.
  return (
    <section className={cn("space-y-3", className)}>
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <Paperclip className="h-4 w-4 text-muted-foreground" />
        Precursor Documents
        {/* Gated on !isError for the same reason as the table below: a refetch
            that fails after a success keeps the previous `data`, so without this
            the header would show a count directly above "unavailable". */}
        {!query.isError && documents.length > 0 && (
          <span className="text-xs font-normal text-muted-foreground">
            ({documents.length})
          </span>
        )}
      </h3>
      <div>
        {query.isPending && (
          <p className="text-sm text-muted-foreground">Loading documents…</p>
        )}

        {/* A 403 (or any other failure) renders a neutral message and never a
            crash: the list simply stays empty for someone who cannot see the
            parent. */}
        {query.isError && (
          <p className="text-sm text-muted-foreground">
            Precursor documents are unavailable for this item.
          </p>
        )}

        {!query.isPending && !query.isError && documents.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No precursor documents attached. Agents attach them with{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              manage_dependencies
            </code>
            .
          </p>
        )}

        {!query.isError && documents.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">File</th>
                  <th className="py-2 pr-3 font-medium">Label</th>
                  <th className="py-2 pr-3 font-medium">Size</th>
                  <th className="py-2 pr-3 font-medium">Uploaded by</th>
                  <th className="py-2 pr-3 font-medium">Uploaded</th>
                  <th className="sr-only py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} className="border-b align-top last:border-0">
                    <td className="py-2 pr-3">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => {
                              setPreviewId(doc.id);
                            }}
                            className="inline-flex max-w-full items-center gap-1.5 text-left font-medium text-link underline-offset-4 hover:underline"
                          >
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{doc.fileName}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {/* originalPath is the uploader own path, kept for
                              provenance only, never used for file operations. */}
                          {doc.originalPath ?? doc.fileName}
                        </TooltipContent>
                      </Tooltip>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {doc.label ?? "—"}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">
                      {formatDocumentSize(doc.sizeBytes)}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {/* The list endpoint returns only uploadedById; resolving
                          it to a display name would need an API-side join, so
                          the short id is shown with the full id on hover. */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="font-mono text-xs">
                            {doc.uploadedById.slice(0, 8)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{doc.uploadedById}</TooltipContent>
                      </Tooltip>
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">
                      {formatUploadedAt(doc.createdAt)}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Download ${doc.fileName}`}
                        disabled={downloadingIds.has(doc.id)}
                        onClick={() => {
                          void handleDownload(doc);
                        }}
                      >
                        {downloadingIds.has(doc.id) ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {downloadError && (
          <p className="mt-2 text-sm text-destructive">{downloadError}</p>
        )}
      </div>

      <DependencyDocumentPreviewDialog
        depId={previewId}
        onClose={() => {
          setPreviewId(null);
        }}
      />
    </section>
  );
}

/**
 * Preview dialog for a single document. The body is only fetched while the
 * dialog is open, because the list endpoint omits the content column.
 */
function DependencyDocumentPreviewDialog({
  depId,
  onClose,
}: {
  depId: string | null;
  onClose: () => void;
}) {
  const query = useDependencyDocument(depId, { enabled: !!depId });
  const doc = query.data;

  return (
    <Dialog
      open={!!depId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="truncate">
            {doc?.fileName ?? "Precursor Document"}
          </DialogTitle>
          <DialogDescription>
            {doc
              ? `${doc.contentType} · ${formatDocumentSize(doc.sizeBytes)}`
              : "Loading document…"}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[65vh] overflow-y-auto">
          {query.isPending && (
            <p className="text-sm text-muted-foreground">Loading document…</p>
          )}

          {query.isError && (
            <p className="text-sm text-muted-foreground">
              This document could not be loaded.
            </p>
          )}

          {doc?.contentType === "text/markdown" ? (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <MarkdownRenderer content={doc.content} />
            </div>
          ) : doc ? (
            <pre className="whitespace-pre-wrap break-words rounded bg-muted p-3 text-xs">
              {doc.content}
            </pre>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Absolute timestamp for the uploaded-at column. Falls back to the raw string
 * if the API ever hands back something Date cannot parse.
 */
function formatUploadedAt(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return format(parsed, "MMM d, yyyy");
}
